import { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { SearchSelect } from '@/components/ui/search-select';
import type { SearchSelectOption } from '@/components/ui/search-select';
import { cn } from '@/lib/utils';
import { migrationV1V2Api } from '../api/migration-v1-v2';
import type { AssignableUser } from '../api/migration-v1-v2';
import type { MigrationProject } from '../types';

/** Valeur sentinelle pour l'option « désassigner » (id 0 impossible côté DB). */
const UNASSIGNED_VALUE = '__none__';

interface AssignUserControlProps {
  /** `null` pour une école « à faire » sans projet encore créé. */
  projectId: number | null;
  /** Requis quand `projectId` est null : un projet (brouillon) est créé à la volée. */
  schoolId?: number;
  assignedTo: { id: number; firstname: string; lastname: string } | null;
  users: AssignableUser[];
  /** Appelé avec le projet renvoyé par l'API après (dé)assignation. */
  onAssigned: (project: MigrationProject) => void;
  /** Notifié en cas d'échec (revert / message géré par le parent). */
  onError?: (message: string) => void;
  className?: string;
  triggerClassName?: string;
}

/**
 * Sélecteur d'assignation/désassignation d'un projet de migration à un
 * utilisateur back-office. À n'afficher que pour un administrateur — le parent
 * est responsable du gating (les non-admins voient un affichage en lecture
 * seule). La liste `users` est fournie par le parent (cf. getAssignableUsers).
 */
export function AssignUserControl({
  projectId,
  schoolId,
  assignedTo,
  users,
  onAssigned,
  onError,
  className,
  triggerClassName,
}: AssignUserControlProps) {
  const [saving, setSaving] = useState(false);

  const options = useMemo<SearchSelectOption[]>(() => {
    const userOptions = users.map((u) => ({
      value: String(u.id),
      label: `${u.firstname} ${u.lastname}`.trim() || u.email || `#${u.id}`,
      searchText: u.email ?? undefined,
    }));
    return [{ value: UNASSIGNED_VALUE, label: 'Non assigné' }, ...userOptions];
  }, [users]);

  const value = assignedTo ? String(assignedTo.id) : '';

  const handleChange = async (next: string) => {
    const nextUserId = next === UNASSIGNED_VALUE ? null : Number(next);
    // No-op si on resélectionne la valeur déjà assignée.
    if ((assignedTo?.id ?? null) === nextUserId) return;

    try {
      setSaving(true);
      let pid = projectId;
      if (pid == null) {
        // École « à faire » : on matérialise un projet (brouillon) pour porter
        // l'assignation. Désassigner une école sans projet est un no-op.
        if (nextUserId == null) return;
        if (schoolId == null) {
          onError?.('Impossible de créer le projet de migration');
          return;
        }
        const created = await migrationV1V2Api.createProject({ schoolId });
        pid = created.id;
      }
      const project = await migrationV1V2Api.assignProject(pid, nextUserId);
      onAssigned(project);
    } catch {
      onError?.("Échec de l'assignation du projet");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={cn('relative inline-flex items-center', className)}>
      <SearchSelect
        options={options}
        value={value}
        onValueChange={handleChange}
        placeholder="Assigner…"
        searchPlaceholder="Rechercher un utilisateur…"
        className={cn(saving && 'opacity-60 pointer-events-none', triggerClassName)}
        contentClassName="min-w-[16rem]"
      />
      {saving && (
        <Loader2 className="pointer-events-none absolute right-8 h-3.5 w-3.5 animate-spin text-muted-foreground" />
      )}
    </div>
  );
}
